/* eslint-disable no-console */
import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../environments/environment';
import { NzMessageService } from 'ng-zorro-antd/message';
import * as Sentry from '@sentry/angular-ivy';
import { signOut as amplifySignOut } from '@aws-amplify/auth';
import { AmplifyError } from '@aws-amplify/core/internals/utils';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private readonly logging: number;
  private messageBeingShown = false;
  public onLine: boolean = navigator.onLine;
  public readonly internalLogs: { date: Date; level: any; value: unknown; stack: string | undefined }[] = [];

  constructor(
    private readonly messageService: NzMessageService,
    private readonly ngZone: NgZone,
  ) {
    switch (environment.logging) {
      case 'error':
        this.logging = 1;
        break;
      case 'warn':
        this.logging = 2;
        break;
      case 'info':
        this.logging = 3;
        break;
      default:
        this.logging = 4;
    }
  }

  private logCaller(level: string, ...args: unknown[]): void {
    const err = new Error(level);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(err);
      console.log(err);
    } else {
      const stack = new Error().stack;
      console.log(stack);
      err.stack = stack;
    }
    this.internalLogs.push({ date: new Date(), level: level, value: args, stack: level === 'error' ? err.stack : '' });
  }

  public debug(...args: unknown[]): void {
    if (this.logging === 4) {
      console.log(args);
      this.logCaller('debug', args);
    }
  }

  public info(...args: any[]): void {
    if (this.logging >= 3) {
      console.log(args);
      this.logCaller('info', args);
    }
  }

  public warn(...args: any[]): void {
    if (this.logging >= 2) {
      console.warn(args);
      this.logCaller('warn', args);
    }
  }

  // if you do not want to show the error to the user, pass false as second parameter
  public error(...args: any[]): void {
    console.error(args);
    this.logCaller('error', args);

    let userMessage = $localize`Oops! An Error occurred. A message has been sent to the platform administrator for analysis.`;
    let errorMessage = '';
    let httpStatusCode = 0;
    if (args[0] instanceof AmplifyError) {
      const amplifyError = args[0] as AmplifyError;
      errorMessage = amplifyError.message;
      Sentry.captureException(amplifyError, {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
      if ((amplifyError as any).$metadata?.httpStatusCode) {
        httpStatusCode = (amplifyError as any).$metadata.httpStatusCode;
      }
    } else if (args[0] instanceof HttpErrorResponse) {
      const httpError = args[0] as HttpErrorResponse;
      Sentry.captureException(httpError, {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
      errorMessage = httpError.message;
      httpStatusCode = httpError.status;
    } else if (args[0] instanceof Error) {
      Sentry.captureException(args[0], {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
      errorMessage = (args[0] as Error).message;
    } else {
      let str;
      if (typeof args[0] === 'string') {
        str = args[0];
      } else {
        try {
          str = JSON.stringify(args[0]);
        } catch (error) {
          str = args[0].toString();
        }
      }
      Sentry.captureMessage(str, {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
    }
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;
    if (errorMessage && chunkFailedMessage.test(errorMessage)) {
      this.messageService.create(
        'warning',
        $localize`A new version of the application has been detected. Reloading...`,
        {
          nzDuration: 7000,
          nzPauseOnHover: true,
        },
      );
      setTimeout(() => {
        if (navigator.onLine) {
          this.reloadApp();
        }
      }, 2500);
      return;
    }
    if (
      errorMessage &&
      errorMessage.indexOf('Failed to load resource: the server responded with a status of 403') !== -1
    ) {
      Sentry.captureMessage('LoggingService-reloadApp (403)', {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
      setTimeout(() => {
        this.reloadApp();
      }, 500);
      return;
    }
    if (errorMessage && errorMessage === 'The user is not authenticated') {
      Sentry.captureMessage('LoggingService-reloadApp (The user is not authenticated)', {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
      setTimeout(() => {
        this.reloadApp();
      }, 500);
      return;
    }
    if (errorMessage && errorMessage.indexOf('No current user') !== -1) {
      Sentry.captureMessage('LoggingService-reloadApp (No current user)', {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
      setTimeout(() => {
        this.reloadApp();
      }, 500);
      return;
    }
    if (errorMessage && errorMessage.indexOf('Invalid login token') !== -1) {
      Sentry.captureMessage('LoggingService-reloadApp (Invalid login token)', {
        tags: {
          online: navigator.onLine,
          networkSpeed: (navigator as any).connection?.downlink,
          rtt: (navigator as any).connection?.rtt,
          saveData: (navigator as any).connection?.saveData,
          networkType: (navigator as any).connection?.type,
          networkEffectiveType: (navigator as any).connection?.effectiveType,
          version: environment.version,
        },
      });
      setTimeout(() => {
        this.reloadApp();
      }, 500);
      return;
    }
    if (
      errorMessage.indexOf('Request failed with status code') !== -1 ||
      errorMessage.indexOf('Http failure response for') !== -1 ||
      httpStatusCode
    ) {
      const statusCode = errorMessage.substring(32);
      if (httpStatusCode === 409 || statusCode === '409') {
        userMessage = $localize`The item you're trying to save has been modified by another source. You should refresh your data and try to save again.`;
      } else if (httpStatusCode === 400 || statusCode === '400') {
        userMessage = $localize`The data you provided is not valid and has been rejected by the server.`;
      } else if (httpStatusCode === 403 || statusCode === '403') {
        userMessage = $localize`You are not authorized to perform this action.`;
      } else if (httpStatusCode === 404 || statusCode === '404') {
        userMessage = $localize`The item was not found.`;
      } else if (httpStatusCode === 504 || statusCode === '504') {
        if (!this.onLine) {
          userMessage = $localize`There seems to be a network problem. You are offline and probably have not previously visited this page when online so it cannot be displayed.`;
        } else {
          userMessage = $localize`There seems to be a network problem. The application cannot function properly.`;
        }
      }
    } else if (errorMessage.indexOf('Network Error') !== -1) {
      if (!this.onLine) {
        userMessage = $localize`There seems to be a network problem. You are offline and probably have not previously visited this page when online so it cannot be displayed.`;
      } else {
        userMessage = $localize`There seems to be a network problem. The application cannot function properly.`;
      }
    }
    if (args[1] === undefined || args[1] !== false) {
      // avoid showing many messages at the same time
      if (!this.messageBeingShown) {
        this.messageBeingShown = true;
        this.messageService.create('error', userMessage, {
          nzDuration: 8000,
          nzPauseOnHover: true,
        });
        setTimeout(() => {
          this.messageBeingShown = false;
        }, 8000);
      }
    }
  }

  private async signOut(reload = true): Promise<void> {
    if (!navigator.onLine) {
      return;
    }
    try {
      await amplifySignOut();
      if (reload) {
        this.ngZone.run(() => {
          if (navigator.onLine) {
            window.location.reload();
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  private reloadApp(): void {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        Promise.all(registrations.map((r) => r.unregister()))
          .then(() => {
            if (navigator.onLine) {
              window.location.reload();
            }
          })
          .catch((error) => {
            console.error(error);
            if (navigator.onLine) {
              window.location.reload();
            }
          });
      })
      .catch((error) => {
        console.error(error);
        if (navigator.onLine) {
          window.location.reload();
        }
      });
  }
}
